import type { ListBuilderConfiguration } from '../types';

/**
 * Competitive Price Client Embedded list configuration.
 * Shows competitive prices embedded in a client detail view.
 */
export const COMPETITIVE_PRICE_CLIENT_EMBEDDED_CONFIG: ListBuilderConfiguration =
	{
		lists: [
			{
				id: 'default-competitive-price-client-embedded',
				cacheTTL: 600000,
				name: 'Client Competitive Prices',
				type: 'default',
				active: true,
				sortOrder: 0,
				columns: [
					{
						id: 'item-info',
						fields: [
							{
								id: 'item-description-field',
								link: {
									type: 'object' as const,
									enabled: true,
									objectTable: 'item',
									idField: 'fk_item__id',
								},
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'itemDescription',
										objectTable: 'competitive_price',
									},
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
							},
							{
								id: 'lb-manufacturer',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'line_break',
							},
							{
								id: 'item-manufacturer-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'itemManufacturer',
										objectTable: 'competitive_price',
									},
									defaultValue: '',
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'theme',
										themeColor: 'textSecondary',
									},
								},
								displayStyle: 'text',
							},
							{
								id: 'lb-external-id',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'line_break',
							},
							{
								id: 'item-id-field',
								dataConfig: {
									type: 'template',
									template:
										'{competitive_price.itemExternalId2|itemExternalId|fk_item__id}',
									templateFields: [],
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'theme',
										themeColor: 'textSecondary',
									},
								},
								displayStyle: 'text',
							},
						],
						headers: [{ id: 'h-item', text: 'Item' }],
						minWidth: 220,
						sortable: true,
						alignment: 'left',
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'itemDescription',
								objectTable: 'competitive_price',
							},
						],
						widthConfig: {
							mode: 'flexible',
							preset: 'large',
						},
						verticalAlignment: 'top',
					},
					{
						id: 'vendor-name',
						fields: [
							{
								id: 'vendor-name-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'vendorName',
										objectTable: 'competitive_price',
									},
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
							},
						],
						headers: [{ id: 'h-vendor', text: 'Vendor Name' }],
						minWidth: 150,
						sortable: true,
						alignment: 'left',
						sortFields: [
							{
								direction: 'ASC',
								fieldName: 'vendorName',
								objectTable: 'competitive_price',
							},
						],
						widthConfig: {
							mode: 'flexible',
							preset: 'medium',
						},
						verticalAlignment: 'middle',
					},
					{
						id: 'price',
						fields: [
							{
								id: 'price-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'price',
										objectTable: 'competitive_price',
									},
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'currency',
							},
							{
								id: 'lb-item-price',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'line_break',
							},
							{
								id: 'item-price-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'itemPrice',
										objectTable: 'competitive_price',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'theme',
										themeColor: 'textSecondary',
									},
								},
								displayStyle: 'currency',
							},
						],
						headers: [
							{ id: 'h-price', text: 'Comp. Price' },
							{ id: 'h-item-price', text: 'Item Price' },
						],
						minWidth: 110,
						sortable: true,
						alignment: 'right',
						sortFields: [
							{
								direction: 'DESC',
								fieldName: 'price',
								objectTable: 'competitive_price',
							},
						],
						widthConfig: {
							mode: 'flexible',
							preset: 'small',
						},
						verticalAlignment: 'top',
					},
					{
						id: 'created-date',
						fields: [
							{
								id: 'created-date-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: '_created_date',
										objectTable: 'competitive_price',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'date',
							},
							{
								id: 'lb-creator',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'line_break',
							},
							{
								id: 'creator-name-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'creatorName',
										objectTable: 'competitive_price',
									},
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'theme',
										themeColor: 'textSecondary',
									},
								},
								displayStyle: 'text',
							},
						],
						headers: [
							{ id: 'h-created', text: 'Created' },
							{ id: 'h-creator', text: 'Created By' },
						],
						minWidth: 130,
						sortable: true,
						alignment: 'left',
						sortFields: [
							{
								direction: 'DESC',
								fieldName: '_created_date',
								objectTable: 'competitive_price',
							},
						],
						widthConfig: {
							mode: 'flexible',
							preset: 'medium',
						},
						verticalAlignment: 'top',
					},
					{
						id: 'file-column',
						fields: [
							{
								id: 'file-name-field',
								dataConfig: {
									type: 'field',
									field: {
										fieldName: 'fileName',
										objectTable: 'competitive_price',
									},
									defaultValue: '',
								},
								styleConfig: {
									size: 'sm',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'text',
								link: {
									type: 'callback' as const,
									enabled: true,
									callbackName: 'viewFile',
								},
							},
						],
						headers: [{ id: 'h-file', text: 'Attachment' }],
						minWidth: 100,
						sortable: false,
						alignment: 'left',
						widthConfig: {
							mode: 'flexible',
							preset: 'small',
						},
						verticalAlignment: 'middle',
					},
					{
						id: 'actions-column',
						fields: [
							{
								id: 'actions-dropdown',
								dataConfig: {
									type: 'custom_text',
									customText: '',
								},
								styleConfig: {
									size: 'md',
									colorRule: {
										type: 'default',
									},
								},
								displayStyle: 'actions_dropdown',
								dropdownActions: [
									{
										id: 'edit-action',
										label: 'Edit',
										iconName: 'edit',
										callbackName: 'edit',
										variant: 'primary',
										visibility: 'visible',
									},
									{
										id: 'delete-action',
										label: 'Delete',
										iconName: 'trash',
										callbackName: 'delete',
										variant: 'danger',
										visibility: 'visible',
									},
								],
							},
						],
						headers: [{ id: 'h-actions', text: '' }],
						width: 80,
						align: 'center',
						sortable: false,
					},
				],
				defaultSortId: 'column-created-date-desc',
			},
		],
		paging: {
			defaultPageSize: 25,
			pageSizeOptions: [10, 25, 50, 100],
		},
		filters: {
			activeFilters: [],
			defaultFilters: {},
		},
		objects: [
			{
				objectLabel: 'Competitive Price',
				objectTable: 'competitive_price',
				selectedFields: [
					{ fieldName: '_id', fieldLabel: 'ID' },
					{ fieldName: 'itemDescription', fieldLabel: 'Item' },
					{ fieldName: 'itemManufacturer', fieldLabel: 'Manufacturer' },
					{ fieldName: 'itemExternalId', fieldLabel: 'Item ID' },
					{ fieldName: 'vendorName', fieldLabel: 'Vendor Name' },
					{ fieldName: 'price', fieldLabel: 'Comp. Price' },
					{ fieldName: 'itemPrice', fieldLabel: 'Item Price' },
					{ fieldName: '_created_date', fieldLabel: 'Created' },
					{ fieldName: 'creatorName', fieldLabel: 'Created By' },
					{ fieldName: 'fileName', fieldLabel: 'Attachment' },
				],
			},
			{
				baseUrl: '/items/:id',
				objectLabel: 'Item',
				objectTable: 'item',
				selectedFields: [{ fieldName: 'fk_item__id', fieldLabel: 'ID' }],
			},
		],
	};
